import type { AdminRole } from '../stores/authStore'

/**
 * The admin permission matrix, as the portal sees it.
 *
 * FALLBACK ONLY. The source of truth is packages/shared/src/utils/adminPermissions.ts,
 * which the API enforces on every admin route and serves at `GET /admin/permissions`
 * (see usePermissions). This copy mirrors it so the portal keeps working against an
 * API that doesn't have that endpoint yet: the portal deploys from main straight to
 * the live site, days ahead of the production API, which deploys from prod. Keep the
 * two in step — when they disagree, the server's answer wins whenever it gives one.
 *
 * Hiding things here is a convenience, not security: the API refuses whatever a
 * role isn't allowed regardless of what the portal shows.
 */

export const ADMIN_SECTIONS = [
  'dashboard',
  'monthlyReport',
  'users',
  'reports',
  'suppliers',
  'supplierCategories',
  'supplierPlans',
  'supplierEnquiries',
  'supplyDashboard',
  // OneMall (Shop products): same level as 'suppliers' for every role. Keep in step with adminPermissions.ts.
  'oneMall',
  'creativeCatalogue',
  'creativeModeration',
  'creativeCreators',
  'creativePipeline',
  'creativeAnalytics',
  'vendors',
  'appSettings',
  'onboarding',
  'jobs',
  'hashtags',
  'notificationCampaigns',
  'affiliateCodes',
  'growthCampaigns',
  'wallets',
  // Per-department monthly reports — keep in step with adminPermissions.ts.
  'reportUsers',
  'reportHashtags',
  'reportWallet',
  'reportSuppliers',
  'reportCreatives',
  'reportJobs',
  'reportOneMall',
  'admins',
  'syntheticBots',
  // High Ticket Ad Space: ad ops vs approvals/reserved slots. Keep in step with adminPermissions.ts.
  'advertising',
  'advertisingApprovals',
  // Voice/video calls and Go Live. Keep in step with adminPermissions.ts.
  'calls',
  'live',
  // Placeholder items for modules not built yet (Business Modules and Mini-Apps
  // groups in the sidebar). Super Admin and Admin only; keep in step with
  // adminPermissions.ts.
  'upcomingModules',
] as const

export type AdminSection = (typeof ADMIN_SECTIONS)[number]

export type AdminAction = 'view' | 'export' | 'create' | 'edit' | 'approve' | 'delete'

/**
 * - `full`: everything, including add, approve and delete
 * - `fullNoDelete`: everything except delete
 * - `edit`: change existing items only — no add, approve or delete
 * - `createEdit`: see it, add new items and change existing ones — no approve,
 *   export or delete
 * - `viewExport`: see it and export its figures
 * - `view`: see it, nothing else
 * - `none`: hidden and refused
 */
export type AdminAccessLevel =
  | 'full'
  | 'fullNoDelete'
  | 'edit'
  | 'createEdit'
  | 'viewExport'
  | 'view'
  | 'none'

const LEVEL_ACTIONS: Record<AdminAccessLevel, readonly AdminAction[]> = {
  full: ['view', 'export', 'create', 'edit', 'approve', 'delete'],
  fullNoDelete: ['view', 'export', 'create', 'edit', 'approve'],
  edit: ['view', 'edit'],
  createEdit: ['view', 'create', 'edit'],
  viewExport: ['view', 'export'],
  view: ['view'],
  none: [],
}

export function isAccessLevel(value: unknown): value is AdminAccessLevel {
  return typeof value === 'string' && value in LEVEL_ACTIONS
}

export function levelAllows(level: AdminAccessLevel, action: AdminAction): boolean {
  return LEVEL_ACTIONS[level]?.includes(action) ?? false
}

export type PermissionMap = Record<AdminSection, AdminAccessLevel>

const ALL_FULL = Object.fromEntries(ADMIN_SECTIONS.map((s) => [s, 'full'])) as PermissionMap
const ALL_NONE = Object.fromEntries(ADMIN_SECTIONS.map((s) => [s, 'none'])) as PermissionMap

// The Editor's row, shared with Marketing (which adds Users).
const EDITOR_ROW: PermissionMap = {
  ...ALL_NONE,
  dashboard: 'viewExport',
  monthlyReport: 'viewExport',
  reports: 'view',
  suppliers: 'view',
  supplierCategories: 'view',
  supplierPlans: 'view',
  supplierEnquiries: 'view',
  supplyDashboard: 'view',
  oneMall: 'view',
  jobs: 'createEdit',
  hashtags: 'view',
  notificationCampaigns: 'createEdit',
  growthCampaigns: 'view',
  wallets: 'view',
  reportUsers: 'viewExport',
  reportHashtags: 'viewExport',
  reportWallet: 'viewExport',
  reportSuppliers: 'viewExport',
  reportCreatives: 'viewExport',
  reportJobs: 'viewExport',
  reportOneMall: 'viewExport',
  calls: 'view',
  live: 'view',
}

export const FALLBACK_PERMISSIONS: Record<AdminRole, PermissionMap> = {
  // Super Admin
  // upcomingModules: 'view' only, since the "Coming soon" pages have nothing to act on.
  admin: { ...ALL_FULL, upcomingModules: 'view' },

  // Admin: everything except managing admin accounts and Synthetic Bots.
  manager: {
    ...ALL_FULL,
    admins: 'none',
    syntheticBots: 'none',
    upcomingModules: 'view',
    advertisingApprovals: 'none',
    // Super Admins only (Todd, 4 Nov 2026; spec: full). See adminPermissions.ts.
    appSettings: 'none',
  },

  // Editor: view-only on a few areas; exports Dashboard and Monthly Report;
  // imports and edits jobs (no delete) — deliberate changes from the
  // 2026-10-21 spec (Todd, 2026-10-22).
  //
  // notificationCampaigns: 'createEdit' (raised from 'view', Todd, 2026-10-31)
  // — Editors draft/edit/test-send campaigns but never send one live; the API
  // gates `POST /:id/send` on the "approve" action, which 'createEdit' does
  // not grant. Keep in step with adminPermissions.ts.
  //
  // growthCampaigns (Marketing tab, 2026-10-23): Editor gets 'view' only,
  // mirroring notificationCampaigns' old shape — keep in step with adminPermissions.ts.
  //
  // wallets (Wallet tab, 2026-10-23): same 'view' shape — keep in step too.
  editor: EDITOR_ROW,

  // Marketing (Todd, 5 Nov 2026 — for Fortune): the Editor's row plus the Users page
  // (view). The API also lets it through the monthly Users report and shows it the
  // Dashboard's restricted rows and the Users stats panels (capabilities.audienceInsights).
  // Keep in step with adminPermissions.ts.
  marketing_manager: { ...EDITOR_ROW, users: 'view' },

  // Supplier: the Suppliers section only; never deletes supplier profiles or products.
  supply_manager: {
    ...ALL_NONE,
    suppliers: 'fullNoDelete',
    supplierCategories: 'full',
    supplierPlans: 'full',
    supplierEnquiries: 'full',
    supplyDashboard: 'full',
    oneMall: 'fullNoDelete',
    reportSuppliers: 'viewExport',
    reportOneMall: 'viewExport',
  },

  // Content Moderator: edits existing creative content; never adds, approves or deletes it.
  content_moderator: {
    ...ALL_NONE,
    creativeCatalogue: 'edit',
    creativeModeration: 'view',
    creativeCreators: 'view',
    creativePipeline: 'view',
    creativeAnalytics: 'view',
    reports: 'view',
    calls: 'view',
    live: 'edit',
  },

  // Creatives (Todd, 4 Nov 2026): the Creatives modules and the monthly Creatives
  // report, full for now; everything else none, as for Supplier. Catalogue,
  // Pipeline Health and Overview are restricted sections the API lets this role
  // through (ROLE_RESTRICTED_SECTION_PASSES). Keep in step with adminPermissions.ts.
  creatives_manager: {
    ...ALL_NONE,
    creativeCatalogue: 'full',
    creativeModeration: 'full',
    creativeCreators: 'full',
    creativePipeline: 'full',
    creativeAnalytics: 'full',
    reportCreatives: 'full',
  },
}

/** The built-in row for a role. An unknown role gets nothing but Security. */
export function fallbackPermissionsFor(role: string | undefined): PermissionMap {
  const row = role ? FALLBACK_PERMISSIONS[role as AdminRole] : undefined
  return { ...(row ?? ALL_NONE) }
}

/**
 * Each section's page, in sidebar order — used to pick where a role lands after
 * sign-in and where a blocked route sends it. `/security` is open to every role
 * and is the last resort.
 */
export const SECTION_HOME: ReadonlyArray<readonly [AdminSection, string]> = [
  ['dashboard', '/dashboard'],
  ['supplyDashboard', '/supply-dashboard'],
  ['users', '/users'],
  ['monthlyReport', '/monthly-report'],
  ['reportJobs', '/monthly-reports/jobs'],
  ['reportHashtags', '/monthly-reports/hashtags'],
  ['reportUsers', '/monthly-reports/users'],
  ['reportWallet', '/monthly-reports/wallet'],
  ['reportCreatives', '/monthly-reports/creatives'],
  ['reportSuppliers', '/monthly-reports/suppliers'],
  ['reportOneMall', '/monthly-reports/onemall'],
  ['suppliers', '/suppliers'],
  ['supplierCategories', '/supplier-categories'],
  ['supplierPlans', '/supplier-plans'],
  ['supplierEnquiries', '/enquiries'],
  ['oneMall', '/onemall/overview'],
  ['creativeAnalytics', '/creative/analytics'],
  ['creativeCatalogue', '/creative/manage'],
  ['creativeModeration', '/creative/videos'],
  ['creativeCreators', '/creative/creators'],
  ['creativePipeline', '/creative/pipeline'],
  ['reports', '/reports'],
  ['vendors', '/vendors'],
  ['appSettings', '/settings'],
  ['onboarding', '/onboarding'],
  ['jobs', '/jobs'],
  ['hashtags', '/hashtags'],
  ['live', '/live'],
  ['calls', '/calls'],
  ['admins', '/admins'],
  ['syntheticBots', '/synthetic-bots'],
  ['notificationCampaigns', '/notification-campaigns'],
  ['affiliateCodes', '/affiliate-campaigns'],
  ['growthCampaigns', '/growth-campaigns'],
  ['wallets', '/wallets'],
  ['advertising', '/advertising/bookings'],
]

export const SECURITY_PATH = '/security'

export function firstAllowedPath(permissions: PermissionMap): string {
  const hit = SECTION_HOME.find(([section]) => levelAllows(permissions[section] ?? 'none', 'view'))
  return hit ? hit[1] : SECURITY_PATH
}
